import React, { useEffect, useMemo, useState } from 'react';

interface FactionMember {
  actorId: number;
  name: string;
  rankId: number;
  rankName: string;
  online: boolean;
  lastSeenAt: number | null;
}

export interface FactionMembersState {
  factionId: string | null;
  factionName?: string;
  memberCount?: number;
  ownRankName?: string;
  members?: FactionMember[];
}

interface NewsPost {
  id: string;
  authorName: string;
  title: string;
  text: string;
  postedAt: number;
}

export interface FactionNewsState {
  factionId: string | null;
  posts: NewsPost[];
}

// "Gestion" sub-tab - mirrors identitySystem.ts's pushFactionPanelState wire
// format exactly, same shape escMenuService.ts already parses (itself ported
// from the now-retired standalone panel, factionPanelService.ts /
// factionPanel/index.tsx). Kept separate from FactionMember/FactionMembersState
// above (the read-only "Membres" sub-tab) since the two come from different
// server systems (escMenuSystem.ts vs identitySystem.ts) and were never
// meant to be reconciled into one shape tonight.
interface FactionPanelMember {
  actorId: number;
  name: string;
  rankId: number;
  rankName: string;
  online: boolean;
}

interface FactionPanelRank {
  id: number;
  name: string;
  maxSlots?: number;
  count: number;
}

interface FactionPanelPermissions {
  canManageMembers?: boolean;
  canManageCharter?: boolean;
  canManageProperty?: boolean;
  canSentence?: boolean;
}

interface HousingRequest {
  refrId: number;
  price: number;
  requesterName: string | null;
}

interface HousingOwnedHouse {
  refrId: number;
  displayName: string;
  ownerName: string | null;
  memberCount: number;
  // 'owned' | 'rented' for an occupied house, 'sale' | 'rent' | 'both' for a
  // vacant one listed by the hold.
  status?: string;
  price?: number;
  rentPricePerWeek?: number | null;
}

// Read-only - see justiceSystem.ts's own comment on why there are no
// sentence buttons here (/sentence stays a chat command).
interface JusticePrisoner {
  actorId: number;
  name: string;
  arrestedByName: string;
  queuedAt: number;
  online: boolean;
}

interface JusticeBounty {
  actorId: number;
  name: string;
  amount: number;
}

interface HousingTreasury {
  taxPerWeek: number;
  canSet: boolean;
  canCollect: boolean;
  balance: number | null;
}

export interface FactionPanelState {
  membership: { factionId: string; rankId: number } | null;
  factionId?: string;
  factionName?: string;
  permissions?: FactionPanelPermissions;
  charter?: string;
  members?: FactionPanelMember[];
  ranks?: FactionPanelRank[];
  propertyRequests?: HousingRequest[];
  ownedHouses?: HousingOwnedHouse[];
  housingTreasury?: HousingTreasury | null;
  prisoners?: JusticePrisoner[];
  bounties?: JusticeBounty[];
}

interface Props {
  factionName: string | null; // from the character sheet, shown immediately without waiting on a members request
  ownRankName: string | null;
  members: FactionMembersState | null;
  news: FactionNewsState | null;
  postResult: { ok: boolean; text: string } | null;
  panel: FactionPanelState | null;
  onRequestMembers: () => void;
  onRequestNews: () => void;
  onPostNews: (title: string, text: string) => void;
  onRequestPanel: () => void;
  onPromote: (actorId: number) => void;
  onDemote: (actorId: number) => void;
  onKick: (actorId: number) => void;
  onLeave: () => void;
  onSetCharter: (text: string) => void;
  onApproveHousing: (refrId: number) => void;
  onDenyHousing: (refrId: number) => void;
  onEvictHousing: (refrId: number) => void;
  onManageHousingChests: (refrId: number) => void;
  onSetHousingTax: (amount: number) => void;
  onCollectHousingTreasury: () => void;
}

type SubView = 'overview' | 'members' | 'news' | 'gestion';
type ManageTab = 'members' | 'housing' | 'justice' | 'charter';

type ConfirmKind = { kind: 'kick' | 'leave'; target?: FactionPanelMember };

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Ranks ordered by id, mirroring identitySystem.ts's adjacentRank - used to
// know which +1/-1 rank a promote/demote button would target, purely to
// enable/disable the button correctly. The server re-validates everything;
// this only avoids offering an action that would just bounce. Ported from
// the retired standalone panel (factionPanel/index.tsx).
function adjacentRank(ranks: FactionPanelRank[], rankId: number, direction: 1 | -1): FactionPanelRank | null {
  const sorted = [...ranks].sort((a, b) => a.id - b.id);
  const idx = sorted.findIndex((r) => r.id === rankId);
  if (idx === -1) return null;
  return sorted[idx + direction] ?? null;
}

const FactionPage: React.FC<Props> = ({
  factionName, ownRankName, members, news, postResult, panel,
  onRequestMembers, onRequestNews, onPostNews,
  onRequestPanel, onPromote, onDemote, onKick, onLeave, onSetCharter, onApproveHousing, onDenyHousing, onEvictHousing,
  onManageHousingChests, onSetHousingTax, onCollectHousingTreasury,
}) => {
  const [subView, setSubView] = useState<SubView>('overview');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
  const [charterDraft, setCharterDraft] = useState('');
  const [editingCharter, setEditingCharter] = useState(false);
  const [taxDraft, setTaxDraft] = useState('');
  const [manageTab, setManageTab] = useState<ManageTab>('members');

  useEffect(() => {
    if (subView === 'members' && !members) onRequestMembers();
    if (subView === 'news' && !news) onRequestNews();
    // The overview shows the charter and rank, which come with the management panel.
    if ((subView === 'gestion' || subView === 'overview' || subView === 'news') && !panel) onRequestPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subView]);

  useEffect(() => {
    if (postResult?.ok) {
      setTitle('');
      setText('');
    }
  }, [postResult]);

  useEffect(() => {
    setCharterDraft(panel?.charter ?? '');
  }, [panel?.charter]);

  useEffect(() => {
    setTaxDraft(panel?.housingTreasury ? String(panel.housingTreasury.taxPerWeek) : '');
  }, [panel?.housingTreasury?.taxPerWeek]);

  const ranks = panel?.ranks ?? [];
  const panelMembers = panel?.members ?? [];
  const ownRankId = panel?.membership?.rankId;
  const ownPanelRank = useMemo(() => ranks.find((r) => r.id === ownRankId), [ranks, ownRankId]);
  const canManageMembers = !!panel?.permissions?.canManageMembers;
  const canManageCharter = !!panel?.permissions?.canManageCharter;
  const canManageProperty = !!panel?.permissions?.canManageProperty;
  const canSentence = !!panel?.permissions?.canSentence;
  const propertyRequests = panel?.propertyRequests ?? [];
  const ownedHouses = panel?.ownedHouses ?? [];
  const housingTreasury = panel?.housingTreasury ?? null;
  const prisoners = panel?.prisoners ?? [];
  const bounties = panel?.bounties ?? [];

  if (!factionName) {
    return <p className="esc-empty">Tu n'appartiens à aucune faction. Un Jarl ou un chef de faction peut te recruter.</p>;
  }

  const submit = () => {
    if (!title.trim() || !text.trim()) return;
    onPostNews(title.trim(), text.trim());
  };

  const doLeave = () => {
    setConfirm(null);
    onLeave();
  };

  const doKick = (member: FactionPanelMember) => {
    setConfirm(null);
    onKick(member.actorId);
  };

  const saveCharter = () => {
    onSetCharter(charterDraft);
    setEditingCharter(false);
  };

  const manageTabs: Array<{ id: ManageTab; label: string }> = [
    { id: 'members', label: 'Membres' },
    ...(canManageProperty || housingTreasury ? [{ id: 'housing' as ManageTab, label: 'Logements' }] : []),
    ...(canSentence ? [{ id: 'justice' as ManageTab, label: 'Justice' }] : []),
    { id: 'charter', label: 'Charte' },
  ];
  const activeManage: ManageTab = manageTabs.some((t) => t.id === manageTab) ? manageTab : 'members';

  const SUBVIEWS: Array<{ id: SubView; label: string; count?: number }> = [
    { id: 'overview', label: "Vue d'ensemble" },
    { id: 'members', label: 'Membres', count: members?.memberCount },
    { id: 'news', label: 'Annonces' },
    { id: 'gestion', label: 'Gestion' },
  ];

  const pendingCount = propertyRequests.length;

  return (
    <div className="esc-faction">
      <nav className="esc-subtabs" aria-label="Sections de la faction">
        {SUBVIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className={'esc-subtab' + (subView === v.id ? ' esc-subtab--active' : '')}
            aria-current={subView === v.id ? 'page' : undefined}
            onClick={() => setSubView(v.id)}
          >
            {v.label}
            {v.count != null && <span className="esc-subtab__count">{v.count}</span>}
            {v.id === 'gestion' && pendingCount > 0 && <span className="esc-subtab__count esc-subtab__count--alert">{pendingCount}</span>}
          </button>
        ))}
      </nav>

      <div className="esc-faction__body">
        {subView === 'overview' && (
          <div className="esc-overview">
            <section className="esc-card esc-overview__id">
              <h2 className="esc-overview__name">{factionName}</h2>
              {ownRankName && (
                <dl className="esc-overview__rank">
                  <dt>Votre rang</dt>
                  <dd>{ownRankName}</dd>
                </dl>
              )}
            </section>
            <section className="esc-card">
              <h3 className="esc-card__title">Charte</h3>
              {!panel ? (
                <p className="esc-empty">Chargement...</p>
              ) : (
                <p className="esc-prose">{panel.charter || "Cette faction n'a pas encore de charte."}</p>
              )}
            </section>
            {housingTreasury && (
              <section className="esc-card">
                <h3 className="esc-card__title">Taxe de fief</h3>
                <p className="esc-prose">
                  {housingTreasury.taxPerWeek > 0
                    ? `${housingTreasury.taxPerWeek} Septims par semaine pour chaque logement acheté dans le fief.`
                    : 'Aucune taxe de fief pour le moment.'}
                </p>
              </section>
            )}
          </div>
        )}

        {subView === 'members' && (
          <section className="esc-card esc-card--flush">
            {!members || !members.members ? (
              <p className="esc-empty">Chargement...</p>
            ) : members.members.length === 0 ? (
              <p className="esc-empty">Aucun membre.</p>
            ) : (
              <ul className="esc-roster">
                {members.members.map((m) => (
                  <li className="esc-roster__row" key={m.actorId}>
                    <span className={'esc-presence' + (m.online ? ' esc-presence--on' : '')} title={m.online ? 'En ligne' : 'Hors ligne'} />
                    <span className="esc-roster__name">{m.name}</span>
                    <span className="esc-roster__rank">{m.rankName}</span>
                    <span className="esc-roster__seen">{m.online ? 'En ligne' : m.lastSeenAt ? `Vu le ${formatDate(m.lastSeenAt)}` : 'Jamais vu'}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {subView === 'news' && (
          <div className="esc-news">
            {canManageCharter && (
              <section className="esc-card">
                <h3 className="esc-card__title">Publier une annonce</h3>
                <div className="esc-form">
                  <label className="esc-label" htmlFor="esc-news-title">Titre</label>
                  <input
                    id="esc-news-title"
                    className="esc-field"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={80}
                  />
                  <label className="esc-label" htmlFor="esc-news-text">Message</label>
                  <textarea
                    id="esc-news-text"
                    className="esc-field esc-field--area"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={1000}
                  />
                  {postResult && !postResult.ok && <p className="esc-form__error">{postResult.text}</p>}
                  <div className="esc-form__actions">
                    <button type="button" className="esc-btn esc-btn--primary" disabled={!title.trim() || !text.trim()} onClick={submit}>
                      Publier
                    </button>
                  </div>
                </div>
              </section>
            )}

            {!news ? (
              <p className="esc-empty">Chargement...</p>
            ) : news.posts.length === 0 ? (
              <p className="esc-empty">Aucune annonce pour le moment.</p>
            ) : (
              <ul className="esc-posts">
                {news.posts.map((p) => (
                  <li className="esc-post" key={p.id}>
                    <div className="esc-post__head">
                      <h3 className="esc-post__title">{p.title}</h3>
                      <time className="esc-post__date">{formatDate(p.postedAt)}</time>
                    </div>
                    <p className="esc-prose">{p.text}</p>
                    <span className="esc-post__author">Par {p.authorName}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {subView === 'gestion' && (
          !panel?.membership ? (
            <p className="esc-empty">Chargement...</p>
          ) : (
            <div className="esc-manage">
              <div className="esc-segments" role="tablist" aria-label="Gestion">
                {manageTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={activeManage === t.id}
                    className={'esc-segment' + (activeManage === t.id ? ' esc-segment--active' : '')}
                    onClick={() => setManageTab(t.id)}
                  >
                    {t.label}
                    {t.id === 'housing' && pendingCount > 0 && <span className="esc-subtab__count esc-subtab__count--alert">{pendingCount}</span>}
                  </button>
                ))}
              </div>

              {activeManage === 'members' && (
                <>
                  <section className="esc-card">
                    <h3 className="esc-card__title">Rangs</h3>
                    <div className="esc-ranks">
                      {ranks.map((r) => (
                        <div key={r.id} className={'esc-rank' + (r.id === ownRankId ? ' esc-rank--own' : '')}>
                          <span className="esc-rank__name">{r.name}</span>
                          <span className="esc-rank__count">{r.count}{r.maxSlots ? ` sur ${r.maxSlots}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="esc-card esc-card--flush">
                    <h3 className="esc-card__title esc-card__title--padded">Membres</h3>
                    {panelMembers.length === 0 ? (
                      <p className="esc-empty">Aucun membre.</p>
                    ) : (
                      <ul className="esc-roster">
                        {panelMembers.map((m) => {
                          const manageable = canManageMembers && ownRankId !== undefined && m.rankId < ownRankId;
                          const up = adjacentRank(ranks, m.rankId, 1);
                          const down = adjacentRank(ranks, m.rankId, -1);
                          const canPromote = manageable && !!up && ownRankId !== undefined && up.id < ownRankId && (!up.maxSlots || up.count < up.maxSlots);
                          const canDemote = manageable && !!down;

                          return (
                            <li className="esc-roster__row" key={m.actorId}>
                              <span className={'esc-presence' + (m.online ? ' esc-presence--on' : '')} title={m.online ? 'En ligne' : 'Hors ligne'} />
                              <span className="esc-roster__name">{m.name}</span>
                              <span className="esc-roster__rank">{m.rankName}</span>
                              {manageable && (
                                <span className="esc-roster__actions">
                                  <button type="button" className="esc-btn esc-btn--sm" disabled={!canPromote} onClick={() => onPromote(m.actorId)}>
                                    Promouvoir
                                  </button>
                                  <button type="button" className="esc-btn esc-btn--sm" disabled={!canDemote} onClick={() => onDemote(m.actorId)}>
                                    Rétrograder
                                  </button>
                                  <button type="button" className="esc-btn esc-btn--sm esc-btn--danger" onClick={() => setConfirm({ kind: 'kick', target: m })}>
                                    Renvoyer
                                  </button>
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                </>
              )}

              {activeManage === 'housing' && (
                <>
                  {housingTreasury && (
                    <section className="esc-card">
                      <h3 className="esc-card__title">Taxe de fief</h3>
                      <div className="esc-kv">
                        {housingTreasury.canCollect && (
                          <div className="esc-kv__row">
                            <span className="esc-kv__label">Caisse</span>
                            <span className="esc-kv__value">{housingTreasury.balance ?? 0} Septims</span>
                            <button
                              type="button"
                              className="esc-btn esc-btn--sm esc-btn--primary"
                              disabled={(housingTreasury.balance ?? 0) <= 0}
                              onClick={onCollectHousingTreasury}
                            >
                              Récolter
                            </button>
                          </div>
                        )}
                        <div className="esc-kv__row">
                          <span className="esc-kv__label">Taxe par semaine</span>
                          {housingTreasury.canSet ? (
                            <>
                              <input
                                className="esc-field esc-field--inline"
                                type="number"
                                min={0}
                                max={5000}
                                aria-label="Taxe par semaine en Septims"
                                value={taxDraft}
                                onChange={(e) => setTaxDraft(e.target.value)}
                              />
                              <button type="button" className="esc-btn esc-btn--sm esc-btn--primary" onClick={() => onSetHousingTax(Number(taxDraft) || 0)}>
                                Enregistrer
                              </button>
                            </>
                          ) : (
                            <span className="esc-kv__value">
                              {housingTreasury.taxPerWeek > 0 ? `${housingTreasury.taxPerWeek} Septims` : 'Aucune'}
                            </span>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {canManageProperty && (
                    <>
                      <section className="esc-card esc-card--flush">
                        <h3 className="esc-card__title esc-card__title--padded">Demandes de logement</h3>
                        {propertyRequests.length === 0 ? (
                          <p className="esc-empty">Aucune demande en attente.</p>
                        ) : (
                          <ul className="esc-roster">
                            {propertyRequests.map((r) => (
                              <li className="esc-roster__row" key={r.refrId}>
                                <span className="esc-roster__name">{r.requesterName ?? 'Inconnu'}</span>
                                <span className="esc-roster__rank">{r.price} Septims</span>
                                <span className="esc-roster__actions">
                                  <button type="button" className="esc-btn esc-btn--sm esc-btn--primary" onClick={() => onApproveHousing(r.refrId)}>
                                    Approuver
                                  </button>
                                  <button type="button" className="esc-btn esc-btn--sm esc-btn--danger" onClick={() => onDenyHousing(r.refrId)}>
                                    Refuser
                                  </button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>

                      <section className="esc-card esc-card--flush">
                        <h3 className="esc-card__title esc-card__title--padded">Logements du fief</h3>
                        {ownedHouses.length === 0 ? (
                          <p className="esc-empty">Aucun logement dans ce fief. Vise une porte avec ALT pour en mettre un en vente ou en location.</p>
                        ) : (
                          <ul className="esc-roster">
                            {ownedHouses.map((h) => (
                              <li className="esc-roster__row" key={h.refrId}>
                                <span className="esc-roster__name">{h.displayName}</span>
                                <span className="esc-roster__rank">
                                  {h.status === 'owned' && `Propriétaire : ${h.ownerName ?? 'inconnu'}`}
                                  {h.status === 'rented' && `Locataire : ${h.ownerName ?? 'inconnu'}`}
                                  {h.status === 'sale' && `À vendre, ${h.price} Septims`}
                                  {h.status === 'rent' && `À louer, ${h.rentPricePerWeek} Septims par semaine`}
                                  {h.status === 'both' && `À vendre ${h.price} Septims ou à louer ${h.rentPricePerWeek} par semaine`}
                                  {!h.status && (h.ownerName ?? 'Inconnu')}
                                  {h.memberCount > 0 ? ` (+${h.memberCount})` : ''}
                                </span>
                                {(h.status === 'owned' || h.status === 'rented' || !h.status) && (
                                  <span className="esc-roster__actions">
                                    <button type="button" className="esc-btn esc-btn--sm" onClick={() => onManageHousingChests(h.refrId)}>
                                      Coffres
                                    </button>
                                    <button type="button" className="esc-btn esc-btn--sm esc-btn--danger" onClick={() => onEvictHousing(h.refrId)}>
                                      Expulser
                                    </button>
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                    </>
                  )}
                </>
              )}

              {activeManage === 'justice' && (
                <>
                  <section className="esc-card esc-card--flush">
                    <h3 className="esc-card__title esc-card__title--padded">Prisonniers</h3>
                    {prisoners.length === 0 ? (
                      <p className="esc-empty">Aucun prisonnier en attente.</p>
                    ) : (
                      <ul className="esc-roster">
                        {prisoners.map((p) => (
                          <li className="esc-roster__row" key={p.actorId}>
                            <span className={'esc-presence' + (p.online ? ' esc-presence--on' : '')} />
                            <span className="esc-roster__name">{p.name}</span>
                            <span className="esc-roster__rank">Arrêté par {p.arrestedByName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="esc-hint esc-hint--padded">Utilise /sentence en jeu pour juger.</p>
                  </section>

                  <section className="esc-card esc-card--flush">
                    <h3 className="esc-card__title esc-card__title--padded">Primes</h3>
                    {bounties.length === 0 ? (
                      <p className="esc-empty">Aucune prime en cours.</p>
                    ) : (
                      <ul className="esc-roster">
                        {bounties.map((b) => (
                          <li className="esc-roster__row" key={b.actorId}>
                            <span className="esc-roster__name">{b.name}</span>
                            <span className="esc-roster__rank">{b.amount} Septims</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </>
              )}

              {activeManage === 'charter' && (
                <>
                  <section className="esc-card">
                    <h3 className="esc-card__title">Charte</h3>
                    {editingCharter ? (
                      <div className="esc-form">
                        <label className="esc-label" htmlFor="esc-charter">Texte de la charte</label>
                        <textarea
                          id="esc-charter"
                          className="esc-field esc-field--area"
                          value={charterDraft}
                          maxLength={2000}
                          onChange={(e) => setCharterDraft(e.target.value)}
                        />
                        <div className="esc-form__actions">
                          <button type="button" className="esc-btn" onClick={() => setEditingCharter(false)}>Annuler</button>
                          <button type="button" className="esc-btn esc-btn--primary" onClick={saveCharter}>Enregistrer</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="esc-prose">{panel.charter || 'Aucune charte pour le moment.'}</p>
                        {canManageCharter && (
                          <div className="esc-form__actions">
                            <button type="button" className="esc-btn" onClick={() => setEditingCharter(true)}>
                              Modifier la charte
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </section>

                  <section className="esc-card">
                    <h3 className="esc-card__title">Appartenance</h3>
                    <p className="esc-prose">Tu perdras ton rang et devras être recruté(e) à nouveau pour revenir.</p>
                    <div className="esc-form__actions">
                      <button type="button" className="esc-btn esc-btn--danger" onClick={() => setConfirm({ kind: 'leave' })}>
                        Quitter la faction
                      </button>
                    </div>
                  </section>
                </>
              )}
            </div>
          )
        )}
      </div>

      {confirm && (
        <div className="esc-dialog-scrim" role="alertdialog" aria-modal="true" aria-labelledby="esc-confirm-title">
          <div className="esc-dialog">
            <h3 className="esc-dialog__title" id="esc-confirm-title">
              {confirm.kind === 'leave' ? 'Quitter la faction ?' : `Renvoyer ${confirm.target?.name} ?`}
            </h3>
            <p className="esc-prose">
              {confirm.kind === 'leave'
                ? 'Tu perdras ton rang et devras être recruté(e) à nouveau pour revenir.'
                : 'Cette personne devra être recrutée à nouveau pour revenir.'}
            </p>
            <div className="esc-form__actions">
              <button type="button" className="esc-btn" onClick={() => setConfirm(null)}>Annuler</button>
              <button
                type="button"
                className="esc-btn esc-btn--danger"
                onClick={() => (confirm.kind === 'leave' ? doLeave() : confirm.target && doKick(confirm.target))}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactionPage;
